import { useMemo } from 'react'
import { collection, query, where } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useBatchedSubscriptions } from './useBatchedSubscriptions'
import type { ArtistInterest } from '../types'

const EMPTY_RECORD: Record<string, string[]> = {}

/**
 * artistId → the given users who follow it, app-wide (profile pages). For who
 * hearted an artist on an event's lineup, use `useFriendEventArtistInterests`.
 * Pass an empty list until the facepiles are on screen.
 */
export function useFriendArtistInterests(userIds: string[]) {
  const { rows, loading } = useBatchedSubscriptions<ArtistInterest>(
    userIds,
    batch =>
      query(
        collection(db, 'artistInterests'),
        where('userId', 'in', batch),
      ),
    d => d.data() as ArtistInterest,
  )

  // Deduped: a user can have more than one interest doc for the same artist
  // (deterministic + auto-seeded write paths don't share an id).
  const artistUserIds = useMemo(() => {
    if (rows.length === 0) return EMPTY_RECORD
    const seen: Record<string, Set<string>> = {}
    rows.forEach(i => {
      ;(seen[i.artistId] ??= new Set()).add(i.userId)
    })
    const map: Record<string, string[]> = {}
    for (const [artistId, ids] of Object.entries(seen)) map[artistId] = [...ids]
    return map
  }, [rows])

  return { artistUserIds, loading }
}
